import { FullScreenModal } from 'V2Components'
import { Button, TargetIcon, TextInput, Typography } from 'procyon-ui'
import React, { useState, useEffect } from 'react'
import { useChannelBrowser } from '../../../providers/hooks'
import { useParams } from 'react-router'
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons'
import { WebhookChannelSelection } from './components/WebhookChannelSelection'
import { ServiceNowChannelSelection } from './components/ServiceNowChannelSelection'
import { getHTMLDataTestID } from 'infra/utils'

const INTEGRATION_TYPES = {
  SLACK: 'SlackIntegration',
  SERVICENOW: 'ServiceNowIntegration',
  MSTEAMS: 'MsTeamsIntegration'
}

const NOTIFICATION_TYPES = {
  GATEWAY: 'getway',
  PRIVATE: 'private',
  APPLICATION: 'application'
}

const ICON_MAP = {
  [INTEGRATION_TYPES.SLACK]: 'SLACK',
  [INTEGRATION_TYPES.SERVICENOW]: 'SERVICENOW',
  [INTEGRATION_TYPES.MSTEAMS]: 'TEAMS'
}

const SelectChannel = (props) => {
  const {
    showModal,
    activeChannel,
    setShowModal,
    onConfirmChannelChange,
    notificationType,
    handleChannelSelect,
    isWebhookChannelSelected: externalWebhookSelected,
    handleChannelSelection: externalHandleSelection
  } = props

  const { linkedChannels } = useChannelBrowser()
  const { integrationName, kind } = useParams()
  const [searchTerm, setSearchTerm] = useState('')

  // Local state for webhook selection
  const [localWebhookSelected, setLocalWebhookSelected] = useState(false)

  // Sync with parent state when modal opens or external state changes
  useEffect(() => {
    if (showModal) {
      setLocalWebhookSelected(externalWebhookSelected || false)
    }
  }, [showModal, externalWebhookSelected])

  const getModalTitle = () => {
    const isSlackOrMsTeams = [INTEGRATION_TYPES.SLACK, INTEGRATION_TYPES.MSTEAMS].includes(kind)

    if (isSlackOrMsTeams && notificationType === NOTIFICATION_TYPES.GATEWAY) {
      return 'Select Channel To Get Gateway Notification'
    }

    if (isSlackOrMsTeams && notificationType === NOTIFICATION_TYPES.PRIVATE) {
      return 'Select Channel To Get Private Notification'
    }

    if (
      kind === INTEGRATION_TYPES.SERVICENOW &&
      notificationType === NOTIFICATION_TYPES.APPLICATION
    ) {
      return 'Select Application Task Table'
    }

    if (kind === INTEGRATION_TYPES.SERVICENOW && notificationType === NOTIFICATION_TYPES.PRIVATE) {
      return 'Select Private Task Table'
    }

    return 'Select Channel'
  }

  const filterChannels = () => {
    const lowerSearchTerm = searchTerm.toLowerCase()

    return linkedChannels.filter((item) => {
      const { ChannelName, Name, ProjectName } = item?.Spec || {}

      // Slack Integration - filter by ChannelName
      if (kind === INTEGRATION_TYPES.SLACK && ChannelName) {
        return ChannelName.toLowerCase().includes(lowerSearchTerm)
      }

      // ServiceNow Integration - filter by Name or ProjectName
      if (kind === INTEGRATION_TYPES.SERVICENOW && !ChannelName) {
        return (
          (Name && Name.toLowerCase().includes(lowerSearchTerm)) ||
          (ProjectName && ProjectName.toLowerCase().includes(lowerSearchTerm))
        )
      }

      // MS Teams Integration - filter by Name
      if (kind === INTEGRATION_TYPES.MSTEAMS && Name) {
        return Name.toLowerCase().includes(lowerSearchTerm)
      }

      return false
    })
  }

  const handleWebhookToggle = (currentState) => {
    const newState = !currentState
    setLocalWebhookSelected(newState)

    // Also update parent state if handler exists
    if (externalHandleSelection) {
      externalHandleSelection(newState)
    }
  }

  const handleCancel = () => {
    setSearchTerm('')
    setLocalWebhookSelected(false)
    setShowModal(false)
  }

  const handleConfirm = () => {
    setSearchTerm('')

    // Pass webhook selection state if applicable
    const channelData = localWebhookSelected ? { isWebhook: true } : activeChannel

    onConfirmChannelChange(channelData, notificationType)
  }

  const renderChannelSelection = () => {
    const filteredChannels = filterChannels()

    switch (kind) {
      case INTEGRATION_TYPES.SLACK:
      case INTEGRATION_TYPES.MSTEAMS:
        return (
          <WebhookChannelSelection
            isWebhookChannelSelected={localWebhookSelected}
            handleChannelSelection={handleWebhookToggle}
          />
        )

      case INTEGRATION_TYPES.SERVICENOW:
        return (
          <ServiceNowChannelSelection
            filteredChannel={filteredChannels}
            selectedChannel={activeChannel}
            handleChannelSelection={handleChannelSelect}
          />
        )

      default:
        return null
    }
  }

  const getIntegrationIcon = () => ICON_MAP[kind] || null

  return (
    <FullScreenModal showModal={showModal}>
      <div className='flex justify-center items-center h-[100%] bg-[#2229455a]'>
        <div className='w-[626px] rounded-lg p-8 bg-white'>
          {/* Header */}
          <div className='flex justify-between flex-col'>
            <Typography variant='title-regular' className='flex text-2xl items-center font-bold'>
              <TargetIcon
                width='48px'
                height='48px'
                type={getIntegrationIcon()}
                className='rounded'
                style={{
                  borderRadius: '12px',
                  marginRight: '15px'
                }}
              />
              {getModalTitle()}
            </Typography>
            <p className='ml-16 font-thin'>{integrationName}</p>
          </div>

          {/* Search Input */}
          <div className='my-5'>
            <TextInput
              data-testid={getHTMLDataTestID('integration', 'input', 'search-channel')}
              icon={faMagnifyingGlass}
              iconPosition='end'
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder='Search'
              value={searchTerm}
              style={{ width: '550px' }}
            />
          </div>

          {/* Channel Selection */}
          <div className='mt-2 rounded text-black max-h-[40vh] overflow-auto'>
            {renderChannelSelection()}
          </div>

          {/* Action Buttons */}
          <div className='flex justify-end mt-10 gap-4'>
            <Button
              data-testid={getHTMLDataTestID('integration', 'button', 'cancel')}
              size='md'
              onClick={handleCancel}
              variant='gray'
            >
              Cancel
            </Button>
            <Button
              data-testid={getHTMLDataTestID('integration', 'button', 'confirm')}
              size='md'
              onClick={handleConfirm}
              variant='primary'
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>
    </FullScreenModal>
  )
}

export { SelectChannel }
